/** Bottom edge of `#app-top-nav` in viewport coordinates (scroll offset target). */
export function getAppTopNavBottomPx(): number {
  const nav = document.getElementById("app-top-nav");
  return nav?.getBoundingClientRect().bottom ?? 80;
}

/**
 * Bottom edge of the painted top nav bar — used to anchor the compact menu
 * panel. On `/viewer` the header shell is transparent and can be taller than
 * `.viewer-top-chrome-slide`; measure the chrome row so the menu meets the
 * visible bar with no map strip in between.
 */
export function getNavMenuAnchorBottomPx(): number {
  const header = document.getElementById("app-top-nav");
  if (!header) return 80;
  const chrome = header.querySelector<HTMLElement>(".viewer-top-chrome-slide");
  return (chrome ?? header).getBoundingClientRect().bottom;
}

/**
 * Scroll so `sectionId`'s top edge meets the bottom of the sticky top nav.
 * Returns false when the target element is not in the document yet.
 */
export function scrollToMarketingSection(
  sectionId: string,
  options?: { behavior?: ScrollBehavior; updateHash?: boolean }
): boolean {
  const id = sectionId.replace(/^#/, "");
  const target = document.getElementById(id);
  if (!target) return false;

  const top =
    target.getBoundingClientRect().top +
    window.scrollY -
    getAppTopNavBottomPx();

  window.scrollTo({
    top: Math.max(0, top),
    behavior: options?.behavior ?? "smooth"
  });

  if (options?.updateHash !== false) {
    const hash = `#${id}`;
    if (window.location.hash !== hash) {
      window.history.pushState(null, "", hash);
    }
  }

  return true;
}

/** Poll until lazy sections mount, then scroll (used after cross-route `/#…` nav). */
export function scrollToMarketingSectionWhenReady(
  sectionId: string,
  maxAttempts = 48
): void {
  let attempts = 0;
  const tryScroll = (): void => {
    if (scrollToMarketingSection(sectionId, { updateHash: false })) return;
    if (++attempts >= maxAttempts) return;
    requestAnimationFrame(tryScroll);
  };
  requestAnimationFrame(tryScroll);
}
